import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { withStyles } from '@material-ui/core/styles'
import {
  Typography,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Button,
  Collapse,
  Box,
  Tabs,
  Tab,
} from '@material-ui/core'
import SettingsIcon from '@material-ui/icons/Settings'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import AddIcon from '@material-ui/icons/Add'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import RefreshIcon from '@material-ui/icons/Refresh'
import MovieIcon from '@material-ui/icons/Movie'
import CircularProgress from '@material-ui/core/CircularProgress'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import styles from './styles'
import {
  GoogleDriveConnect,
  ProcessStep,
  AnalyzeStep,
  GenerateStep,
  SettingsPanel,
  TaskManager,
} from './components'
import useGoogleDrive from './hooks/useGoogleDrive'
import useProcessDocuments from './hooks/useProcessDocuments'
import useAnalyzeStream from './hooks/useAnalyzeStream'
import useGenerateStream from './hooks/useGenerateStream'
import useSession from './hooks/useSession'
import {
  headlessGoogleDriveFiles,
  headlessCbSettingsGet,
  headlessCbSettingsPatch,
  headlessHeygenCredits,
} from 'src/api/headless'

const STEPS = [
  'Connect Drive',
  'Process Documents',
  'Analyze Content',
  'Generate Courses',
]

const HeygenCreditsBadge = () => {
  const [state, setState] = useState({ loading: true, configured: null, remaining: null, error: null })

  const fetchCredits = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }))
    try {
      const res = await headlessHeygenCredits()
      const data = res?.data || res
      setState({
        loading: false,
        configured: !!data?.configured,
        remaining: data?.remaining_quota ?? null,
        error: null,
      })
    } catch (e) {
      setState({ loading: false, configured: null, remaining: null, error: e?.message || 'Lookup failed' })
    }
  }, [])

  useEffect(() => {
    fetchCredits()
  }, [fetchCredits])

  let body
  if (state.loading && state.configured === null) {
    body = <CircularProgress size={14} style={{ color: '#64748B' }} />
  } else if (state.error) {
    body = <Typography style={{ fontSize: '13px', color: '#EF4444', fontWeight: 600 }}>HeyGen credits unavailable</Typography>
  } else if (state.configured === false) {
    body = <Typography style={{ fontSize: '13px', color: '#94A3B8', fontWeight: 600 }}>HeyGen key not configured</Typography>
  } else {
    const formatted = typeof state.remaining === 'number' ? state.remaining.toLocaleString() : '—'
    body = (
      <Typography style={{ fontSize: '13px', color: '#1E293B', fontWeight: 600 }}>
        HeyGen credits: <span style={{ color: '#3B82F6' }}>{formatted}</span>
      </Typography>
    )
  }

  return (
    <Box
      display="flex"
      alignItems="center"
      style={{
        padding: '6px 12px',
        borderRadius: '999px',
        backgroundColor: '#FFFFFF',
        border: '1px solid #E2E8F0',
        gap: '8px',
      }}
    >
      <MovieIcon style={{ fontSize: '16px', color: '#3B82F6' }} />
      {body}
      <Tooltip title="Refresh HeyGen credits">
        <span>
          <IconButton size="small" onClick={fetchCredits} disabled={state.loading} style={{ padding: '2px' }}>
            {state.loading ? (
              <CircularProgress size={12} style={{ color: '#64748B' }} />
            ) : (
              <RefreshIcon style={{ fontSize: '14px', color: '#64748B' }} />
            )}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  )
}

const buildGenerateData = (analyzeResult, organizeResult, session) => {
  const collectionName = session?.currentSession?.folder_name || 'My Learning Path'
  
  let organized = organizeResult
  if (!organized || organized.length === 0) {
    // Fallback: Group by file if organizeResult is missing
    organized = (analyzeResult.attachments || []).map(att => ({
      courseTitle: att.file_name?.split(' — ')[0].replace(/\.pdf$/i, '') || att.chapter_title || 'New Course',
      chapters: [{ ...att }]
    }))
  }

  // Build a lookup from course_title -> attachment_ids using the analyze result
  const courseAttachmentMap = {}
  for (const c of (analyzeResult.courses || [])) {
    if (c.course_title) {
      courseAttachmentMap[c.course_title] = c.attachment_ids || []
    }
  }

  const courses = organized.map(course => {
    return {
      title: course.courseTitle,
      goals: [],
      attachment_ids: courseAttachmentMap[course.courseTitle] || [],
    }
  })
  
  return {
    topic: collectionName,
    title: collectionName,
    attachment_ids: analyzeResult.attachments?.map(a => a.attachment_id) || [],
    language_code: 'en',
    document_following: 'follow',
    image_source: 'imageGen',
    image_generation_model: 'gpt-image-2.5',
    courses,
    collection_name: collectionName,
    goals: [],
    session_id: session?.currentSession?.session_id || null,
  }
}

const CollectionBuilder = props => {
  const { classes } = props
  const history = useHistory()
  const location = useLocation()
  const [view, setView] = useState(null)
  const [activeStep, setActiveStep] = useState(0)
  const [selectedFolder, setSelectedFolder] = useState(null)
  const [selectedFiles, setSelectedFiles] = useState([])
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [statusFilter, setStatusFilter] = useState('active') // 'all', 'active', 'completed', 'failed', 'cancelled'
  const [generateVideo, setGenerateVideo] = useState(() => {
    try {
      return localStorage.getItem('cb_generateVideo') === 'true'
    } catch {
      return false
    }
  })
  const [backupIntroVideosToDrive, setBackupIntroVideosToDrive] = useState(() => {
    try {
      return localStorage.getItem('cb_backupIntroVideosToDrive') === 'true'
    } catch {
      return false
    }
  })
  const [avatarId, setAvatarId] = useState(() => {
    try {
      return localStorage.getItem('cb_avatarId') || ''
    } catch {
      return ''
    }
  })

  // Load persisted settings from server on mount (overrides localStorage)
  useEffect(() => {
    headlessCbSettingsGet()
      .then(data => {
        if (data) {
          if (typeof data.generate_video === 'boolean') {
            setGenerateVideo(data.generate_video)
            try { localStorage.setItem('cb_generateVideo', data.generate_video ? 'true' : 'false') } catch {}
          }
          if (typeof data.backup_intro_videos_to_drive === 'boolean') {
            setBackupIntroVideosToDrive(data.backup_intro_videos_to_drive)
            try { localStorage.setItem('cb_backupIntroVideosToDrive', data.backup_intro_videos_to_drive ? 'true' : 'false') } catch {}
          }
          if (typeof data.avatar_id === 'string') {
            setAvatarId(data.avatar_id)
            try { localStorage.setItem('cb_avatarId', data.avatar_id) } catch {}
          }
        }
      })
      .catch(() => {}) // fall back to localStorage values
  }, [])

  // Reset backup toggle when generate video is turned off
  useEffect(() => {
    if (!generateVideo && backupIntroVideosToDrive) {
      setBackupIntroVideosToDrive(false)
      try { localStorage.setItem('cb_backupIntroVideosToDrive', 'false') } catch {}
      headlessCbSettingsPatch({ backup_intro_videos_to_drive: false }).catch(() => {})
    }
  }, [generateVideo]) // eslint-disable-line react-hooks/exhaustive-deps
  const initialUrlHandled = useRef(false)


  const googleDrive = useGoogleDrive()
  const processDocuments = useProcessDocuments()
  const analyzeStream = useAnalyzeStream()
  const generateStream = useGenerateStream()
  const session = useSession()


  // Read URL params
  const searchParams = new URLSearchParams(location.search)
  const urlSessionId = searchParams.get('session')
  const urlView = searchParams.get('view')

  // Update URL without navigation
  const updateUrl = useCallback((params) => {
    const sp = new URLSearchParams()
    if (params.session) sp.set('session', params.session)
    if (params.view) sp.set('view', params.view)
    const search = sp.toString()
    const newPath = `/collection-builder${search ? `?${search}` : ''}`
    history.replace(newPath)
  }, [history])

  // Fetch sessions and check Google Drive status on mount
  useEffect(() => {
    session.fetchSessions()
    googleDrive.checkStatus()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-expand settings when Google Drive is not connected
  const driveStatusChecked = useRef(false)
  useEffect(() => {
    if (driveStatusChecked.current) return
    if (!googleDrive.loading && session.loaded) {
      driveStatusChecked.current = true
      if (!googleDrive.connected) {
        setSettingsOpen(true)
      }
    }
  }, [googleDrive.loading, googleDrive.connected, session.loaded])

  // Poll sessions every 5s when viewing the sessions list
  useEffect(() => {
    if (view !== 'sessions') return
    const interval = setInterval(() => {
      session.fetchSessions()
    }, 5000)
    return () => clearInterval(interval)
  }, [view]) // eslint-disable-line react-hooks/exhaustive-deps

  // Filtered sessions
  const filteredSessions = useMemo(() => {
    if (!session.sessions) return []
    if (statusFilter === 'all') return session.sessions
    if (statusFilter === 'active') return session.sessions.filter(s => s.status === 'in_progress')
    return session.sessions.filter(s => s.status === statusFilter)
  }, [session.sessions, statusFilter])

  // Step 0: Folder selected
  const handleFolderSelected = useCallback((folder, files) => {
    setSelectedFolder(folder)
    setSelectedFiles(files) // Select all by default
  }, [])

  // Step 0 -> Step 1
  const handleGoogleDriveNext = useCallback(async () => {
    if (selectedFiles && selectedFiles.length > 0) {
      setActiveStep(1)
      if (selectedFolder) {
        const fileIds = selectedFiles.map(f => f.id)
        processDocuments.startProcessing(selectedFolder.id, null, fileIds)

        if (!session.currentSession) {
          const created = await session.createSession({
            folder_id: selectedFolder.id,
            folder_name: selectedFolder.name,
            label: selectedFolder.name,
            current_step: 1,
            file_ids: fileIds,
          })
          if (created && created.session_id) {
            updateUrl({ session: created.session_id })
          }
        } else {
          session.updateSession(session.currentSession.session_id, {
            current_step: 1,
          })
        }
      }
    }
  }, [selectedFiles, selectedFolder, processDocuments, session, updateUrl])

  // Step 1 -> Step 2
  const handleProcessComplete = useCallback((organizedCourses) => {
    setActiveStep(2)
    analyzeStream.startAnalyze(organizedCourses)

    if (session.currentSession) {
      session.updateSession(session.currentSession.session_id, {
        current_step: 2,
        organize_result: organizedCourses,
      })
    }
  }, [session, analyzeStream])

  // Step 2 -> Step 3: Analyze complete, auto-generate
  const handleAnalyzeComplete = useCallback(
    result => {
      const organizeResult = session.currentSession?.organize_result
      const generateData = buildGenerateData(result, organizeResult, session)
      generateData.generate_video = generateVideo
      generateData.backup_intro_videos_to_drive = backupIntroVideosToDrive
      if (generateVideo && avatarId.trim()) {
        generateData.avatar_id = avatarId.trim()
      }
      setActiveStep(3)
      generateStream.startGenerate(generateData).catch(() => {})

      if (session.currentSession) {
        session.updateSession(session.currentSession.session_id, {
          current_step: 3,
          analyze_result: result,
        })
      }
    },
    [generateStream, session, generateVideo, backupIntroVideosToDrive, avatarId],
  )

  // Track job IDs
  useEffect(() => {
    if (processDocuments.jobId && session.currentSession) {
      session.updateSession(session.currentSession.session_id, {
        process_job_id: processDocuments.jobId,
      })
    }
  }, [processDocuments.jobId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (analyzeStream.jobId && session.currentSession) {
      session.updateSession(session.currentSession.session_id, {
        analyze_job_id: analyzeStream.jobId,
      })
    }
  }, [analyzeStream.jobId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (generateStream.jobId && session.currentSession) {
      session.updateSession(session.currentSession.session_id, {
        generate_job_id: generateStream.jobId,
      })
    }
  }, [generateStream.jobId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Track completion/failure
  useEffect(() => {
    if (processDocuments.result && session.currentSession) {
      session.updateSession(session.currentSession.session_id, {
        process_result: processDocuments.result,
      })
    }
  }, [processDocuments.result]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (generateStream.result && session.currentSession) {
      session.updateSession(session.currentSession.session_id, {
        status: 'completed',
      })
    }
  }, [generateStream.result]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (generateStream.error && session.currentSession) {
      session.updateSession(session.currentSession.session_id, {
        status: 'failed',
      })
    }
  }, [generateStream.error]) // eslint-disable-line react-hooks/exhaustive-deps

  // Handlers
  const handleReset = useCallback(() => {
    setActiveStep(0)
    setSelectedFolder(null)
    setSelectedFiles([])
    googleDrive.reset()
    session.clearCurrentSession()
    session.fetchSessions()
    setView('sessions')
    updateUrl({})
  }, [session, updateUrl, googleDrive])

  const handleNewSession = useCallback(() => {
    setActiveStep(0)
    setSelectedFolder(null)
    setSelectedFiles([])
    googleDrive.reset()
    session.clearCurrentSession()
    setView('creation')
    updateUrl({ view: 'new' })
  }, [session, updateUrl, googleDrive])

  const handleResumeSession = useCallback(
    async sessionId => {
      const s = await session.resumeSession(sessionId)
      if (!s) return

      if (s.folder_id) {
        setSelectedFolder({ id: s.folder_id, name: s.folder_name || '' })
        headlessGoogleDriveFiles(s.folder_id)
          .then(data => {
            const files = data.files || []
            // Restore only the originally selected files if file_ids were saved
            if (s.file_ids && s.file_ids.length > 0) {
              const selected = files.filter(f => s.file_ids.includes(f.id))
              setSelectedFiles(selected.length > 0 ? selected : files)
            } else {
              setSelectedFiles(files)
            }
          })
          .catch(() => {})
      }

      const step = s.current_step || 0
      setActiveStep(step)
      setView('creation')
      updateUrl({ session: sessionId })

      // Resume is OBSERVE-ONLY — never create new jobs, only restore state or reconnect

      // Completed → show result
      if (s.status === 'completed') {
        if (s.generate_result) generateStream.setResultDirectly(s.generate_result)
        return
      }

      // Cancelled/failed → go back to last safe step
      if (s.status === 'cancelled' || s.status === 'failed') {
        session.updateSession(sessionId, { status: 'in_progress' })
        if (s.analyze_result) {
          setActiveStep(3)
          analyzeStream.setResultDirectly(s.analyze_result)
        } else if (s.organize_result) {
          setActiveStep(2)
        } else if (s.process_result) {
          setActiveStep(1)
        } else if (s.folder_id) {
          setActiveStep(1)
        }
        return
      }

      // Step 1: Process
      if (step === 1) {
        if (s.process_result) {
          processDocuments.setResultDirectly(s.process_result)
        } else if (s.process_job_id) {
          processDocuments.reconnectToJob(s.process_job_id).catch(() => {})
        } else if (s.folder_id) {
          processDocuments.startProcessing(s.folder_id)
        }
        return
      }

      // Step 2 with result already → show confirmation button
      if (step === 2 && s.analyze_result) {
        analyzeStream.setResultDirectly(s.analyze_result)
        return
      }

      // Step 2 without result → observe existing job (SSE polls MongoDB, safe to reconnect)
      if (step === 2 && s.analyze_job_id) {
        analyzeStream.reconnectToJob(s.analyze_job_id).catch(() => {})
        return
      }

      // Step 3 → reconnect to observe generate (poll-based SSE, safe to reconnect)
      if (step === 3 && s.generate_job_id) {
        generateStream.reconnectToJob(s.generate_job_id).catch(() => {})
        return
      }
    },
    [session, processDocuments, analyzeStream, generateStream, updateUrl],
  )

  const handleCancelSession = useCallback(async sessionId => {
    await session.cancelSession(sessionId)
  }, [session])

  const handleBackToSessions = useCallback(() => {
    setView('sessions')
    session.fetchSessions()
    updateUrl({})
  }, [session, updateUrl])

  // Initial routing
  useEffect(() => {
    if (!session.loaded || initialUrlHandled.current) return

    if (urlSessionId) {
      initialUrlHandled.current = true
      handleResumeSession(urlSessionId)
    } else if (urlView === 'new') {
      initialUrlHandled.current = true
      setView('creation')
      updateUrl({ view: 'new' })
    } else if (view === null) {
      initialUrlHandled.current = true
      setView(session.totalSessions > 0 ? 'sessions' : 'creation')
    }
  }, [session.loaded, urlSessionId, urlView]) // eslint-disable-line react-hooks/exhaustive-deps

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <GoogleDriveConnect
            googleDrive={googleDrive}
            onFolderSelected={handleFolderSelected}
            onNext={handleGoogleDriveNext}
            selectedFiles={selectedFiles}
            onSelectionChange={setSelectedFiles}
          />
        )
      case 1:
        return <ProcessStep processDocuments={processDocuments} onProcessComplete={handleProcessComplete} selectedFolder={selectedFolder} selectedFiles={selectedFiles} />
      case 2:
        return <AnalyzeStep analyzeStream={analyzeStream} onAnalyzeComplete={handleAnalyzeComplete} />
      case 3:
        return <GenerateStep generateStream={generateStream} onReset={handleReset} />
      default:
        return null
    }
  }


  const renderSettings = () => (
    <Box>
      <div className={classes.settingsToggle} onClick={() => setSettingsOpen(prev => !prev)}>
        <Box display="flex" alignItems="center">
          <SettingsIcon style={{ color: '#64748B', marginRight: '16px' }} />
          <div>
            <Typography style={{ fontWeight: 600, color: '#1E293B', fontSize: '15px', lineHeight: 1.2 }}>
              Connection Settings
            </Typography>
            <Typography style={{ color: '#64748B', fontSize: '13px', marginTop: '2px' }}>
              Google Drive & API Keys
            </Typography>
          </div>
        </Box>
        {settingsOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
      </div>
      <Collapse in={settingsOpen}>
        <div className={classes.settingsContent}>
          <SettingsPanel
            googleDrive={googleDrive}
            generateVideo={generateVideo}
            onGenerateVideoChange={val => {
              setGenerateVideo(val)
              try { localStorage.setItem('cb_generateVideo', val ? 'true' : 'false') } catch {}
              headlessCbSettingsPatch({ generate_video: val }).catch(() => {})
            }}
            backupIntroVideosToDrive={backupIntroVideosToDrive}
            onBackupIntroVideosToDriveChange={val => {
              setBackupIntroVideosToDrive(val)
              try { localStorage.setItem('cb_backupIntroVideosToDrive', val ? 'true' : 'false') } catch {}
              headlessCbSettingsPatch({ backup_intro_videos_to_drive: val }).catch(() => {})
            }}
            avatarId={avatarId}
            onAvatarIdChange={val => {
              setAvatarId(val)
              try { localStorage.setItem('cb_avatarId', val) } catch {}
              headlessCbSettingsPatch({ avatar_id: val }).catch(() => {})
            }}
          />
        </div>
      </Collapse>
    </Box>
  )

  if (view === null) return null

  return (
    <div className={classes.root}>
      <div className={classes.body}>
        <div className={classes.header}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" style={{ gap: '16px' }}>
            <div>
              <Typography className={classes.title}>Collection Builder</Typography>
              <Typography className={classes.subtitle}>Import from Google Drive and let AI generate your course collections.</Typography>
            </div>
            <HeygenCreditsBadge />
          </Box>
        </div>

        {view === 'sessions' && (
          <>
            {renderSettings()}

            <div className={classes.card}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography className={classes.sectionTitle}>
                  Your Sessions
                  {session.totalSessions > 0 && (
                    <Typography component="span" style={{ color: '#94A3B8', fontWeight: 400, fontSize: '14px' }}>
                      ({session.totalSessions})
                    </Typography>
                  )}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleNewSession}
                  className={classes.primaryButton}
                  style={{ height: '40px' }}
                >
                  New Session
                </Button>
              </Box>

              <Box mb={2} borderBottom="1px solid #F1F5F9">
                <Tabs
                  value={statusFilter}
                  onChange={(e, val) => setStatusFilter(val)}
                  indicatorColor="primary"
                  textColor="primary"
                  style={{ minHeight: '40px' }}
                >
                  <Tab label="Active" value="active" style={{ textTransform: 'none', minWidth: '80px', fontSize: '13px', fontWeight: 600 }} />
                  <Tab label="Success" value="completed" style={{ textTransform: 'none', minWidth: '80px', fontSize: '13px', fontWeight: 600 }} />
                  <Tab label="Failed" value="failed" style={{ textTransform: 'none', minWidth: '80px', fontSize: '13px', fontWeight: 600 }} />
                  <Tab label="Cancelled" value="cancelled" style={{ textTransform: 'none', minWidth: '80px', fontSize: '13px', fontWeight: 600 }} />
                  <Tab label="All" value="all" style={{ textTransform: 'none', minWidth: '80px', fontSize: '13px', fontWeight: 600 }} />
                </Tabs>
              </Box>

              <TaskManager
                sessions={filteredSessions}
                loading={session.loading}
                onResume={handleResumeSession}
                onCancel={handleCancelSession}
              />
            </div>
          </>
        )}

        {view === 'creation' && (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <div className={classes.backLink} onClick={handleBackToSessions}>
                <ArrowBackIcon fontSize="small" />
                Back to Sessions
              </div>
            </Box>

            {renderSettings()}

            <Paper className={classes.stepperPaper} elevation={0}>
              <Stepper activeStep={activeStep} alternativeLabel className={classes.stepper}>
                {STEPS.map(label => (
                  <Step key={label}><StepLabel>{label}</StepLabel></Step>
                ))}
              </Stepper>
            </Paper>

            <div className={classes.card}>
              {renderStep()}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default withStyles(styles)(CollectionBuilder)
